import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { 
  submitProductDevelopmentAPI, 
  updateProductDevelopmentAPI, 
  fetchFGStatusesAPI,
  updateProjectDetailsAPI,
  updateProjectStatusesAPI,
  updateProjectDocumentDeliverablesAPI,
  updateCampaignDetailsAPI
} from "../stepperAPI";
import ProductDetails from "./ProductDetails";
import StatusSelection from "./StatusSelection";
import DocumentDeliverables from "./DocumentDeliverables";
import AddCampaignID from "./AddCampaignID";
import StyledDialog from "../../../../../../common/models/Dialog";
import { useStyles } from './ReviewAndAddProjectStyles';

// Step names from stepper - these should match exactly what's shown in the stepper
// Import from stepper to ensure consistency (excluding the last step which is "Review And Add Project")
const STEP_NAMES = [
  "Project Details",
  "Status Selection",
  "Document Deliverables",
  "Add Campaign ID",
];

const STORAGE_KEYS = {
  FORM_DATA: "productDevelopmentFormData",
  CURRENT_STEP: "productDevelopmentCurrentStep",
  COMPLETED_STEPS: "productDevelopmentCompletedSteps",
};

const ReviewAndAddProject = ({
  formData,
  classes,
  handleBack,
  handleSubmit,
  handleGoToStep,
  activeStep,
  updateStepData,
  markApiCallSuccessful,
  isEditMode = false,
  isEditCampaign = false,
}) => {
  const localClasses = useStyles();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const campaignSectionRef = useRef(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [sectionSaving, setSectionSaving] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });
  // Track which sections have been changed/edited
  const [changedSections, setChangedSections] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });
  // Store original form data to compare changes
  const [originalFormData, setOriginalFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      return saved ? JSON.parse(saved) : formData;
    } catch {
      return formData;
    }
  });

  // Auto-scroll to campaign section when editing campaign (but don't auto-enter edit mode)
  useEffect(() => {
    if (isEditCampaign) {
      // Just scroll to the section, don't set editingSection
      // User needs to click "EDIT" to proceed
      setTimeout(() => {
        campaignSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [isEditCampaign]);

  // Sync with sessionStorage on mount to ensure we have latest data
  useEffect(() => {
    try {
      const savedFormData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (savedFormData) {
        const parsedData = JSON.parse(savedFormData);
        // Update originalFormData to track changes
        setOriginalFormData(parsedData);
      } else {
        // If no saved data, use formData prop as original
        setOriginalFormData(formData);
      }
    } catch (error) {
      // If error, use formData prop as original
      setOriginalFormData(formData);
    }
  }, []);


  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Get latest data from sessionStorage
      let latestData;
      try {
        const savedFormData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
        latestData = savedFormData ? JSON.parse(savedFormData) : formData;
      } catch (storageError) {
        latestData = formData;
      }

      if (isEditMode) {
        // EDIT MODE: Build payload with flags for changed sections
        console.log("=== EDIT PROJECT - UPDATE PAYLOAD ===");
        console.log("Changed Sections:", changedSections);
        
        // Build flags object
        const flags = {};
        if (changedSections.productDetails) flags.updateProductDetailsFlag = true;
        if (changedSections.fgStatuses) flags.updateFGStatusesFlag = true;
        if (changedSections.documentDeliverables) flags.updateDocumentDeliverablesFlag = true;
        if (changedSections.campaignID) flags.updateCampaignIDFlag = true;
        
        console.log("Flags being sent:", flags);
        
        const payload = {
          ...latestData,
          ...flags,
        };

        console.log("Full Payload:", payload);
        console.log("Payload Keys:", Object.keys(payload));
        
        // Log each section's data if it was changed
        if (changedSections.productDetails) {
          console.log("📦 Product Details Data:", payload.productDetails);
        }
        if (changedSections.fgStatuses) {
          console.log("📊 Status Selection Data:", payload.fgStatuses);
        }
        if (changedSections.documentDeliverables) {
          console.log("📄 Document Deliverables Data:", payload.documentDeliverables);
        }
        if (changedSections.campaignID) {
          console.log("🎯 Campaign ID Data:", payload.campaignID);
        }
        
        console.log("=== END PAYLOAD ===");

        const response = await updateProductDevelopmentAPI(payload);
        
        if (response && response.success) {
          console.log("✅ Update successful:", response);
          // Reset changed sections after successful update
          setChangedSections({
            productDetails: false,
            fgStatuses: false,
            documentDeliverables: false,
            campaignID: false,
          });
          // Update original form data
          setOriginalFormData(latestData);
          setShowSuccessPopup(true);
        }
      } else {
        // ADD MODE: Submit new project
        console.log("=== ADD NEW PROJECT - SUBMIT PAYLOAD ===");
        console.log("Full Payload:", latestData);
        console.log("Payload Keys:", Object.keys(latestData));
        console.log("Product Details:", latestData.productDetails);
        console.log("Status Selection:", latestData.fgStatuses);
        console.log("Document Deliverables:", latestData.documentDeliverables);
        console.log("Campaign ID:", latestData.campaignID);
        console.log("Column Configurations:", latestData.campaignID?.columnConfigurations);
        console.log("=== END PAYLOAD ===");
        
        const response = await submitProductDevelopmentAPI(latestData);
        
        if (response && response.success) {
          console.log("✅ Submit successful:", response);
          setShowSuccessPopup(true);
        }
      }
    } catch (error) {
      console.error("❌ Error in handleSave:", error);
      // Still show success for now (fake API)
      setShowSuccessPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for OK button in success popup
  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);

    // Only after the user has seen and acknowledged the success popup,
    // proceed with navigation by calling handleSubmit
    if (typeof handleSubmit === 'function') {
      handleSubmit();
    }
  };

  const handleEdit = (stepIndex) => {
    // Capture current state as original when entering edit mode
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (saved) {
        const parsedData = JSON.parse(saved);
        setOriginalFormData(parsedData);
      }
    } catch (error) {
      console.error("Error capturing original state:", error);
    }
    setEditingSection(stepIndex);
  };

  const handleSaveSection = async (stepIndex, stepName) => {
    // Prevent duplicate saves for the same section
    if (sectionSaving[stepName]) {
      return;
    }

    try {
      setSectionSaving((prev) => ({ ...prev, [stepName]: true }));

      // Get latest data from sessionStorage
      let latestData;
      try {
        const savedFormData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
        latestData = savedFormData ? JSON.parse(savedFormData) : formData;
      } catch (storageError) {
        latestData = formData;
      }

      // Extract IDs from formData
      const collabrixProjectID = latestData?.productDetails?.collabrixProjectID || 
                                  latestData?.collabrixProjectID || null;
      const collabrixCampaignID = latestData?.campaignID?.collabrixCampaignID || 
                                  latestData?.collabrixCampaignID || null;
      const cdmoOrgID = latestData?.productDetails?.cdmoOrgID || 
                        latestData?.cdmoOrgID || null;
      const sponsorOrgID = latestData?.productDetails?.sponsorOrgID || 
                           latestData?.sponsorOrgID || null;

      console.log(`📡 Calling API for section: ${stepName}`, {
        collabrixProjectID,
        collabrixCampaignID,
        cdmoOrgID,
        sponsorOrgID
      });

      let apiResponse = null;

      // Call respective API based on section
      switch (stepName) {
        case "productDetails": {
          const stepData = latestData?.productDetails || {};
          
          // Format dates properly (handle moment objects, Date objects, or strings)
          const formatDate = (dateValue) => {
            if (!dateValue) return null;
            if (dateValue.format) {
              // moment/dayjs object
              return dateValue.toISOString ? dateValue.toISOString() : dateValue.format("YYYY-MM-DD");
            }
            if (dateValue instanceof Date) {
              return dateValue.toISOString();
            }
            // Already a string, return as is
            return dateValue;
          };
          
          // Handle deliverableType: if it's an array, we need to convert to IDs
          // If deliverableTypeIDs already exists, use it; otherwise try to convert
          let deliverableTypeIDs = stepData.deliverableTypeIDs || "";
          if (!deliverableTypeIDs && Array.isArray(stepData.deliverableType) && stepData.deliverableType.length > 0) {
            // If we have deliverableType array but no IDs, we'd need the mapping
            // For now, log a warning - in production, you'd need access to deliverableTypes list
            console.warn("deliverableType array found but deliverableTypeIDs not available. Using empty string.");
          }
          
          // Build payload similar to ProductDetails component
          const payload = {
            collabrixProductID: stepData.collabrixProductID || null,
            collabrixProjectID: collabrixProjectID || null, // null for new, ID for update
            sponsorOrgID: sponsorOrgID,
            cdmoOrgList: cdmoOrgID ? [{ cdmoOrgID: cdmoOrgID }] : [],
            projectStartDate: formatDate(stepData.projectStartDate),
            projectEndDate: formatDate(stepData.projectEndDate),
            deliverableTypeIDs: deliverableTypeIDs,
            projectName: stepData.projectID || "",
            projectStatus: "",
            projectStatusID: null,
          };
          apiResponse = await updateProjectDetailsAPI(payload);
          
          // Update collabrixProjectID if returned from API (for new projects)
          if (apiResponse?.data?.collabrixProjectID && !collabrixProjectID) {
            const updatedData = { ...latestData };
            if (!updatedData.productDetails) updatedData.productDetails = {};
            updatedData.productDetails.collabrixProjectID = apiResponse.data.collabrixProjectID;
            updatedData.collabrixProjectID = apiResponse.data.collabrixProjectID;
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(updatedData));
          }
          break;
        }

        case "fgStatuses": {
          const stepData = latestData?.fgStatuses || {};
          // Map statuses to API format (similar to StatusSelection component)
          const mapStatusesToAPI = (statuses, type) => {
            if (!Array.isArray(statuses)) return [];
            return statuses
              .filter(s => s.activated)
              .map(s => ({
                statusType: type,
                statusID: s.status_id || s.statusID,
                statusName: s.status_name || s.statusName,
              }));
          };

          const allStatuses = [
            ...mapStatusesToAPI(stepData.projectStatuses || [], "project"),
            ...mapStatusesToAPI(stepData.campaignStatuses || [], "campaign"),
            ...mapStatusesToAPI(stepData.changeControlStatuses || [], "changeControl"),
            ...mapStatusesToAPI(stepData.deviationStatuses || [], "deviation"),
            ...mapStatusesToAPI(stepData.inProcessTestStatuses || [], "inProcessTest"),
            ...mapStatusesToAPI(stepData.productReleaseTestStatuses || [], "productReleaseTest"),
          ];

          const payload = {
            collabrixProjectID: collabrixProjectID,
            cdmoOrgID: cdmoOrgID,
            statuses: allStatuses,
          };
          apiResponse = await updateProjectStatusesAPI(payload);
          break;
        }

        case "documentDeliverables": {
          const stepData = latestData?.documentDeliverables || {};
          const documents = stepData.documents || [];
          
          // Map documents to API format
          const documentDetailsList = documents
            .filter(doc => doc.documentTypeID && doc.fileFormatTypeID && doc.orgResponsibleID)
            .map(doc => ({
              documentTypeID: doc.documentTypeID,
              fileFormatID: doc.fileFormatTypeID,
              uploadOrgCollaboratorTypeID: doc.orgResponsibleID,
              documentCategoryID: doc.documentCategoryID || null,
              documentGroupID: doc.documentGroupID || null,
            }));

          const payload = {
            collabrixProjectID: collabrixProjectID,
            cdmoOrgID: cdmoOrgID,
            documentDetailsList: documentDetailsList,
          };
          apiResponse = await updateProjectDocumentDeliverablesAPI(payload);
          break;
        }

        case "campaignID": {
          const stepData = latestData?.campaignID || {};
          
          // Format dates properly (handle moment/dayjs objects, Date objects, or strings)
          const formatCampaignDate = (dateValue) => {
            if (!dateValue) return null;
            if (dateValue.format) {
              // moment/dayjs object
              return dateValue.format("YYYY-MM-DD");
            }
            if (dateValue instanceof Date) {
              return dateValue.toISOString().split('T')[0]; // YYYY-MM-DD format
            }
            // Already a string, return as is
            return dateValue;
          };
          
          // Get sponsorMosymphonyID (similar to AddCampaignID component)
          const sponsorMosymphonyID = sponsorOrgID || null;
          
          // Build payload similar to AddCampaignID component
          const payload = {
            campaignDetails: {
              collabrixProjectID: collabrixProjectID,
              collabrixCampaignID: collabrixCampaignID || null, // null for new, ID for update
              campaignID: stepData.campaignID || "",
              poNumber: stepData.poNumber || "",
              productQuantityRequired: stepData.productQuantity || "",
              uom: stepData.uom || "KG",
              viewType: stepData.defaultView || "System",
              campaignStartDate: formatCampaignDate(stepData.campaignStartDate),
              campaignEndDate: formatCampaignDate(stepData.campaignEndDate),
              manufacturingFacility: stepData.manufacturingFacility || "",
              dataGranularityID: stepData.dataGranularityID || null,
              cdmoOrgID: cdmoOrgID,
              numberOfStages: stepData.numberOfStages ? parseInt(stepData.numberOfStages) : null,
              campaignStatusID: stepData.campaignStatusID || null,
              campaignStatusText: stepData.campaignStatusText || null,
              sponsorMosymphonyID: sponsorMosymphonyID,
              vendorMosymphonyID: cdmoOrgID, // Assuming cdmoOrgID is the vendor ID
            },
            campaignStageDetails: stepData.campaignStageDetails || [],
            campaignBOMDetails: stepData.campaignBOMDetails || [],
            campaignTabConfiguration: stepData.campaignTabConfiguration || [],
            campaignColumnConfiguration: stepData.campaignColumnConfiguration || [],
          };
          apiResponse = await updateCampaignDetailsAPI(payload);
          
          // Update collabrixCampaignID if returned from API (for new campaigns)
          if (apiResponse?.data?.collabrixCampaignID && !collabrixCampaignID) {
            const updatedData = { ...latestData };
            if (!updatedData.campaignID) updatedData.campaignID = {};
            updatedData.campaignID.collabrixCampaignID = apiResponse.data.collabrixCampaignID;
            updatedData.collabrixCampaignID = apiResponse.data.collabrixCampaignID;
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(updatedData));
          }
          break;
        }

        default:
          console.warn(`Unknown section: ${stepName}`);
      }

      if (apiResponse && apiResponse.status) {
        console.log(`✅ API call successful for ${stepName}:`, apiResponse);
        
        // Mark this section as changed
        setChangedSections((prev) => {
          const updated = {
            ...prev,
            [stepName]: true,
          };
          console.log(`💾 Section "${stepName}" saved. Changed sections:`, updated);
          return updated;
        });

        // Update original form data to track future changes
        try {
          const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
          if (saved) {
            const parsedData = JSON.parse(saved);
            setOriginalFormData(parsedData);
            console.log(`📝 Section "${stepName}" data saved to sessionStorage`);
          }
        } catch (error) {
          console.error(`Error saving section "${stepName}":`, error);
        }

        // Exit edit mode
        setEditingSection(null);
      } else {
        throw new Error(apiResponse?.message || `Failed to save ${stepName}`);
      }
    } catch (error) {
      console.error(`❌ Error saving section "${stepName}":`, error);
      // Still exit edit mode and mark as changed (for now, until error handling UI is added)
      setEditingSection(null);
      // You might want to show an error message to the user here
    } finally {
      setSectionSaving((prev) => ({ ...prev, [stepName]: false }));
    }
  };

  const handleCancelEdit = () => {
    // Cancel editing - exit edit mode without saving
    // Revert to original data
    try {
      if (originalFormData) {
        sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(originalFormData));
        // Trigger update to refresh form data
        if (updateStepData) {
          // Force re-render by updating formData
          window.dispatchEvent(new Event('storage'));
        }
      }
    } catch (error) {
      console.error("Error reverting changes:", error);
    }
    setEditingSection(null);
  };

  // Helper function to check if a section has changes
  const hasSectionChanges = (stepName) => {
    try {
      const currentData = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      if (!currentData || !originalFormData) return false;
      
      const current = JSON.parse(currentData);
      const original = originalFormData;
      
      // Compare section data
      if (stepName === "productDetails") {
        return JSON.stringify(current.productDetails || {}) !== JSON.stringify(original.productDetails || {});
      } else if (stepName === "fgStatuses") {
        return JSON.stringify(current.fgStatuses || {}) !== JSON.stringify(original.fgStatuses || {});
      } else if (stepName === "documentDeliverables") {
        return JSON.stringify(current.documentDeliverables || []) !== JSON.stringify(original.documentDeliverables || []);
      } else if (stepName === "campaignID") {
        return JSON.stringify(current.campaignID || {}) !== JSON.stringify(original.campaignID || {});
      }
      return false;
    } catch (error) {
      console.error("Error checking section changes:", error);
      return false;
    }
  };

  // Monitor formData changes to enable/disable save buttons
  const [sectionHasChanges, setSectionHasChanges] = useState({
    productDetails: false,
    fgStatuses: false,
    documentDeliverables: false,
    campaignID: false,
  });

  // Check for changes whenever editingSection changes - use interval to monitor real-time changes
  useEffect(() => {
    if (editingSection !== null) {
      const stepNames = ["productDetails", "fgStatuses", "documentDeliverables", "campaignID"];
      const stepIndex = editingSection;
      const stepName = stepNames[stepIndex];
      
      if (stepName) {
        // Check immediately
        const hasChanges = hasSectionChanges(stepName);
        setSectionHasChanges(prev => ({
          ...prev,
          [stepName]: hasChanges,
        }));
        
        // Set up interval to check for changes while editing
        const interval = setInterval(() => {
          const hasChangesNow = hasSectionChanges(stepName);
          setSectionHasChanges(prev => ({
            ...prev,
            [stepName]: hasChangesNow,
          }));
        }, 300); // Check every 300ms
        
        return () => clearInterval(interval);
      }
    } else {
      // Reset all changes when not editing
      setSectionHasChanges({
        productDetails: false,
        fgStatuses: false,
        documentDeliverables: false,
        campaignID: false,
      });
    }
  }, [editingSection, originalFormData]);

  /**
   * REVIEW AND EDIT PAGE - SAME COMPONENT FOR BOTH ADD AND EDIT MODES
   * 
   * This component is used for BOTH:
   * 1. Review page (from "Add New Project" flow - final step of stepper)
   * 2. Edit page (from "Edit Project" action - directly navigates to review page)
   * 
   * KEY BEHAVIOR (Same as Commercial Supply):
   * 
   * 1. **All Sections Read-Only by Default**: When the page loads, ALL sections display
   *    data but are NOT editable. Nothing is editable until user clicks "Edit" on a section.
   * 
   * 2. **Section-Level Editing**: When user clicks "Edit" on a section:
   *    - Only THAT section becomes editable (readOnly=false for that section)
   *    - All OTHER sections remain read-only
   *    - Edit buttons on other sections are disabled
   *    - Main "Save Project" button is disabled while editing
   * 
   * 3. **Reuse Existing Step Components**: Instead of duplicating review display logic,
   *    we reuse the same step components (ProductDetails, StatusSelection, etc.) that
   *    already have built-in read-only/view mode support.
   * 
   * 4. **Single Source of Truth**: The step components already know how to display
   *    their data in both edit and view modes. By reusing them, we ensure consistency
   *    - if a step component changes, the review page automatically reflects those changes.
   * 
   * 5. **No Back Buttons in Sections**: Using `hideStepActions={true}` hides the internal
   *    navigation buttons (Back/Next) that step components normally show. This is correct
   *    because in review page, sections are not part of a stepper flow - they're just
   *    review/edit sections. The main page has the back button for navigation.
   * 
   * 6. **Edit Mode State**: `editingSection` state tracks which section (if any) is
   *    currently being edited. When null, all sections are read-only. When set to a
   *    stepIndex, only that section is editable.
   */
  const getStepComponentProps = (stepIndex, stepName) => ({
    formData,
    updateStepData,
    classes,
    handleNext: () => {},
    handleSubmit: () => {},
    activeStep: stepIndex,
    stepsLength: 5,
    markApiCallSuccessful,
    // CRITICAL: All sections are read-only by default until user clicks "Edit"
    // Only the section being edited (editingSection === stepIndex) becomes editable
    readOnly: editingSection !== stepIndex, 
    hideStepActions: true, // Hide back/next buttons - sections don't need navigation
    ...(stepName === "statusSelection" && { fetchFGStatusesAPI }),
  });


  return (
    <Box>
      {/* Hide title in edit mode - main header already shows "Edit Project" */}
      {/* In add mode, show "Project configuration done" */}
      {!isEditMode && (
        <Typography 
          variant="h6" 
          className={classes?.formTitle}
          style={{
            marginBottom: "24px",
          }}
        >
          Project configuration done
        </Typography>
      )}

      {/* Project Details Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[0]}
          </Typography>
          {editingSection === 0 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.productDetails}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(0, "productDetails")}
                disabled={!sectionHasChanges.productDetails || sectionSaving.productDetails}
                style={{
                  backgroundColor: sectionHasChanges.productDetails ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.productDetails ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(0)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // Disable edit if another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <ProductDetails {...getStepComponentProps(0, "productDetails")} />
        </Box>
      </Paper>

      <Divider style={{ margin: "24px 0" }} />

      {/* Status Selection Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[1]}
          </Typography>
          {editingSection === 1 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.fgStatuses}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(1, "fgStatuses")}
                disabled={!sectionHasChanges.fgStatuses || sectionSaving.fgStatuses}
                style={{
                  backgroundColor: sectionHasChanges.fgStatuses ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.fgStatuses ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(1)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // Disable edit if another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <StatusSelection {...getStepComponentProps(1, "statusSelection")} />
        </Box>
      </Paper>

      <Divider style={{ margin: "24px 0" }} />

      {/* Document Deliverables Section */}
      <Paper elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[2]}
          </Typography>
          {editingSection === 2 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.documentDeliverables}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(2, "documentDeliverables")}
                disabled={!sectionHasChanges.documentDeliverables || sectionSaving.documentDeliverables}
                style={{
                  backgroundColor: sectionHasChanges.documentDeliverables ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.documentDeliverables ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(2)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // Disable edit if another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <DocumentDeliverables {...getStepComponentProps(2, "documentDeliverables")} />
        </Box>
      </Paper>

      <Divider style={{ margin: "24px 0" }} />

      {/* Add Campaign ID Section */}
      <Paper ref={campaignSectionRef} elevation={0} style={{ padding: "24px", marginBottom: "24px", border: "1px solid #e0e0e0" }}>
        <Box className={localClasses.sectionHeader}>
          <Typography variant="h6" className={localClasses.sectionTitle}>
            {STEP_NAMES[3]}
          </Typography>
          {editingSection === 3 ? (
            <Box>
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                style={{
                  marginRight: "8px",
                  textTransform: "none",
                  color: "#424242",
                  borderColor: "#e0e0e0",
                  padding: "6px 16px",
                }}
                size="small"
                disabled={sectionSaving.campaignID}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveSection(3, "campaignID")}
                disabled={!sectionHasChanges.campaignID || sectionSaving.campaignID}
                style={{
                  backgroundColor: sectionHasChanges.campaignID ? "#ec6535" : "#e0e0e0",
                  color: "#fff",
                  textTransform: "none",
                  padding: "6px 16px",
                }}
                size="small"
              >
                {sectionSaving.campaignID ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          ) : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => handleEdit(3)}
              className={localClasses.editButton}
              size="small"
              disabled={editingSection !== null} // Disable edit if another section is being edited
            >
              EDIT
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: "24px" }}>
          <AddCampaignID 
            {...getStepComponentProps(3, "campaignID")} 
          />
        </Box>
      </Paper>

      {/* Navigation Buttons - Main page has back button, sections don't */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "80px",
          paddingTop: "24px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSubmitting || isRedirecting || editingSection !== null}
          className={classes?.button}
          style={{
            backgroundColor: "#ec6535",
            color: "#fff",
            padding: "10px 32px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            boxShadow: "0 2px 4px rgba(236, 101, 53, 0.3)",
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
              {isEditMode ? "Updating..." : "Submitting..."}
            </>
          ) : (
            isEditMode ? "Update Project" : "Save Project"
          )}
        </Button>
      </Box>

      {/* Full Page Loader */}
      {(isSubmitting || isRedirecting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      {/* Success Popup Modal */}
      <Dialog
        open={showSuccessPopup}
        onClose={handlePopupOk}
        fullWidth
        className="project-added-success-popup"
        maxWidth="sm"
      >
        <StyledDialog
          title={isEditMode ? "Project Updated" : "New Project Added"}
          subtitle={isEditMode 
            ? "The project details have been updated successfully."
            : "The project details have been added successfully."}
          icon={false} // false means show success (check), true means error (cross)
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>
    </Box>
  );
};

export default ReviewAndAddProject;
